import { useState } from "preact/hooks";
import {
  styled,
  ToucanEmptyNote,
  ToucanFab,
  ToucanSearchField,
  useSearchDocked,
} from "toucan";
import { PersonCard } from "../components/PersonCard.tsx";
import { SortMenu } from "../components/SortMenu.tsx";
import { TagFilter } from "../components/TagFilter.tsx";
import {
  filterPeople,
  initials,
  masteryDetail,
  personMastery,
  personTags,
  sortPeople,
  sortedTags,
  tagLabel,
} from "../model.ts";
import { go } from "../routes.ts";
import { store } from "../state.ts";
import type { PersonCardData, Person, PersonSort } from "../model.ts";

const Page = styled("section")`
  min-height: 100%;
  /* Warm canvas backdrop so the white cards and inputs read as raised
     surfaces; the header keeps its own white. */
  background: var(--toucan-canvas);

  /* The review list: photo-forward cards. One compact column on phones, a
     spacious grid on wide screens — same controls, different density. */
  .people-toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 1rem 0.25rem;
  }

  /* The search box gives up whatever width the sort control needs, since a
     sort pill that shrinks stops naming the order. */
  .people-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .people-controls .search {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
  }

  .people-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 0.75rem 1rem 5rem;
  }

  @media (min-width: 720px) {
    .people-grid {
      /* Wide enough for a face and its words. In rem, since the root font
         grows with the viewport: a px floor would give wide screens more
         columns of bigger type and the narrowest measure. */
      grid-template-columns: repeat(auto-fill, minmax(22rem, 1fr));
      gap: 1rem;
      padding: 1rem 1.5rem 5rem;
    }
  }

  /* The empty-list note is a grid item; span the whole row so it uses the
     width instead of being squeezed into one auto-fill column. */
  .people-grid .panel-empty {
    grid-column: 1 / -1;
    text-align: center;
  }

  /* People's main scrolls, so the capture button pins to the viewport rather
     than to the page it would scroll away with. */
  & > .fab {
    position: fixed;
    right: 16px;
    bottom: 16px;
  }
`;

/** What a browse-list card shows for one person. */
function cardData(person: Person): PersonCardData {
  const data: PersonCardData = {
    name: person.name,
    initials: initials(person.name),
    tags: personTags(store(), person).map(tagLabel),
    mastery: personMastery(store(), person.id),
    masteryDetail: masteryDetail(store(), person.id),
    photos: [...person.photos],
  };
  if (person.note !== undefined) {
    data.note = person.note;
  }
  return data;
}

/** The people matching the search and tag filter, in the order asked for. */
function People({
  query,
  activeTagIds,
  sort,
}: {
  query: string;
  activeTagIds: string[];
  sort: PersonSort;
}) {
  if (store().people.length === 0) {
    return (
      <ToucanEmptyNote>
        No people yet — tap + when you meet someone.
      </ToucanEmptyNote>
    );
  }
  const shown = sortPeople(filterPeople(store(), query, activeTagIds), sort);
  if (shown.length === 0) {
    return <ToucanEmptyNote>Nobody matches.</ToucanEmptyNote>;
  }
  return (
    <>
      {shown.map((person) => (
        <PersonCard
          key={person.id}
          data={cardData(person)}
          onEdit={() => {
            go(`edit/${person.id}`);
          }}
        />
      ))}
    </>
  );
}

/** The browse list, with search and order, the tag filter and review bar. */
export function ListPage({
  activeTagIds,
  onFilter,
  sort,
  onSort,
  query,
  onQuery,
}: {
  activeTagIds: string[];
  onFilter: (tagId: string | undefined) => void;
  sort: PersonSort;
  onSort: (sort: PersonSort) => void;
  query: string;
  onQuery: (query: string) => void;
}) {
  // Search and sort share the top line while the tags fit, and the sort moves
  // down beside their pill once they fold. With the search docked, the sort
  // has the tags' line throughout.
  const docked = useSearchDocked();
  const [folded, setFolded] = useState(false);
  return (
    <Page>
      <div class="people-toolbar">
        <div class="people-controls">
          <ToucanSearchField
            class="search"
            docked
            label="Search people"
            placeholder="Search people…"
            query={query}
            onQuery={onQuery}
          />
          {!docked && !folded && <SortMenu sort={sort} onSort={onSort} />}
        </div>
        <TagFilter
          tags={sortedTags(store().tags)}
          activeTagId={activeTagIds[0]}
          onFilter={onFilter}
          aside={<SortMenu sort={sort} onSort={onSort} />}
          asideOnceFolded={!docked}
          onFold={setFolded}
        />
      </div>
      <div class="people-grid">
        <People query={query} activeTagIds={activeTagIds} sort={sort} />
      </div>
      <ToucanFab
        class="fab"
        bar
        label="Add a person"
        onClick={() => {
          go("add");
        }}
      />
    </Page>
  );
}
